/**
 * Pure numeric shaping for the scan tool's own visual feedback (three states, per the owner's
 * headset-session request: live selection while sweeping, a pulse during the transcription
 * round trip, a fade-out on "nothing found"). Kept Babylon-free the same way `FadeAnimation.ts`
 * is kept separate from `RoomView`'s own mesh/material plumbing: `NullEngine` cannot rasterize
 * anything, so "does it look like a subtle pulse" is not a claim this test suite can make — but
 * "does the computed scale advance correctly as simulated time passes" is a plain function of
 * numbers in and numbers out, and is exactly as testable here as `fadeAlphaAt` already is for
 * the unrelated delete-fade.
 *
 * `RoomView` is the only caller — it owns the mesh and the material this drives (each committed
 * stroke already has its own per-stroke `StandardMaterial`, `RoomView.rebuildStrokeMesh`), and
 * applies whatever scale these functions compute to that material's `emissiveColor` each frame.
 * This file has no opinion about either.
 */

/** Smoothstep — the same easing `FadeAnimation.ts`'s own `ease` uses, so the fade-out here reads
 *  the same as every other eased transition in this project rather than picking its own curve. */
function smoothstep(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
}

/**
 * The emissive scale for a stroke currently caught by an in-flight scan (state 2, "pulsing
 * during the round trip") — a sine wave centred on `baseline` with the given `amplitude`, one
 * full cycle every `periodSeconds`. `elapsedSeconds` is simulated time since the pulse began
 * (or since `RoomView` started tracking it — the phase only matters relative to itself, not to
 * any wall-clock origin), so this is trivially testable by simulated time rather than a real
 * render loop or a fake clock.
 */
export function pulseScale(
  elapsedSeconds: number,
  baseline: number,
  amplitude: number,
  periodSeconds: number,
): number {
  if (periodSeconds <= 0) {
    return baseline;
  }

  const phase = (elapsedSeconds / periodSeconds) * 2 * Math.PI;
  return baseline + amplitude * Math.sin(phase);
}

/** One step of a glow fade-out — the scale to paint this instant, and whether the fade has
 *  reached `baseline` and the caller may stop tracking it. */
export interface GlowFadeStep {
  readonly scale: number;
  readonly done: boolean;
}

/**
 * The emissive scale `elapsedMs` after a scan's glow started easing back to `baseline` (state
 * 3, "fade out on nothing found") from `startScale` — the pulse's own current value at the
 * moment the fade began, so the fade continues smoothly from wherever the pulse happened to be
 * rather than snapping to a fixed peak first. `durationMs` of zero or less resolves instantly,
 * the same degenerate-input handling `fadeAlphaAt` gives a zero-duration fade.
 */
export function glowFadeOutAt(elapsedMs: number, durationMs: number, startScale: number, baseline: number): GlowFadeStep {
  if (durationMs <= 0) {
    return { scale: baseline, done: true };
  }

  const t = elapsedMs / durationMs;

  if (t >= 1) {
    return { scale: baseline, done: true };
  }

  return { scale: startScale + (baseline - startScale) * smoothstep(t), done: false };
}
